import type { GameState, PurchaseKind, Team } from '../sim/types';
import { PROFILES } from '../sim/profiles';
import { TICK_RATE } from '../sim/constants';
import { alivePop, buildingCost, popCap, SHOP } from '../sim/sim';
import { hasTech, techBlockReason, TECHS, waveUnlocked } from '../sim/tech';
import { squadLeader } from '../sim/squads';
import { plotOf } from '../sim/world';
import { WORLD_HALF_X, WORLD_HALF_Z } from '../sim/sim';

// DOM-overlay HUD: roster, army panel, unit/squad panel, shop, tech tree
// overlay, king-danger banner.

const CSS = `
.hud { position: fixed; inset: 0; pointer-events: none; color: #e8ecf2;
       text-shadow: 0 1px 3px rgba(0,0,0,.7); font-size: 12px; }
.hud .panel { background: rgba(13,19,36,.6); border: 1px solid rgba(110,145,220,.28);
       border-radius: 6px; padding: 6px 10px; }
.hud .morale { display:inline-block; width: 34px; height: 5px; vertical-align: middle;
       background: rgba(255,255,255,.16); border-radius: 3px; overflow: hidden;
       margin-left: 6px; }
.hud .morale i { display:block; height:100%; background:#6fbf5a; }
.hud .morale i.low { background:#d0985a; }
.hud .morale i.broken { background:#d05a5a; }
.hud .map { position: absolute; bottom: 76px; left: 14px; background: rgba(13,19,36,.6);
       border: 1px solid rgba(110,145,220,.28); border-radius: 6px; }
.hud .roster { position: absolute; top: 14px; left: 14px; display: flex;
       flex-direction: column; gap: 8px; }
.hud .char { background: rgba(13,19,36,.6); border: 1px solid rgba(110,145,220,.28);
       border-radius: 6px; padding: 6px 10px; width: 190px; }
.hud .char.controlled { border-color: #f0d060; box-shadow: 0 0 8px rgba(240,208,96,.35); }
.hud .char .name { font-size: 12px; letter-spacing: .12em; text-transform: uppercase;
       opacity: .85; margin-bottom: 4px; }
.hud .bar { height: 8px; background: rgba(255,255,255,.15); border-radius: 4px;
       overflow: hidden; }
.hud .bar + .bar { margin-top: 4px; height: 6px; }
.hud .bar .fill { height: 100%; background: #6fbf5a; border-radius: 4px; }
.hud .bar .fill.stamina { background: #5a9fd0; }
.hud .bar .fill.stamina.exhausted { background: #d0985a; }
.hud .char.dead .fill { background: #7a2f2f; }
.hud .units { position: absolute; top: 14px; left: 216px; width: 195px; line-height: 1.6; }
.hud .units .row { display: flex; justify-content: space-between; }
.hud .units .head { opacity: .75; letter-spacing: .1em; text-transform: uppercase;
       font-size: 11px; }
.hud .units .key { color: #f0d060; }
.hud .hint { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%);
       font-size: 12px; opacity: .75; background: rgba(10,14,22,.55);
       padding: 5px 14px; border-radius: 6px; white-space: nowrap; }
.hud .army { position: absolute; bottom: 12px; left: 14px; line-height: 1.6; }
.hud .army .order { color: #f0d060; text-transform: uppercase; letter-spacing: .1em; }
.hud .gold { color: #f0d060; }
.hud .shop { position: absolute; bottom: 12px; right: 14px; line-height: 1.65;
       min-width: 185px; }
.hud .shop .row { display: flex; justify-content: space-between; gap: 14px; }
.hud .row.dim { opacity: .45; }
.hud .key { color: #f0d060; }
.hud .head { opacity: .75; letter-spacing: .1em; text-transform: uppercase;
       font-size: 11px; margin-bottom: 2px; }
.hud .fps { position: absolute; top: 10px; right: 14px; font-size: 11px;
       opacity: .6; font-variant-numeric: tabular-nums; }
.hud .alert { position: absolute; top: 64px; left: 50%; transform: translateX(-50%);
       background: rgba(120,20,20,.85); border: 1px solid #ff6a5a; color: #ffe0d8;
       font-size: 16px; letter-spacing: .15em; padding: 8px 22px; border-radius: 6px;
       animation: hudpulse .9s ease-in-out infinite; }
@keyframes hudpulse { 50% { opacity: .55; } }
.hud .tech { position: absolute; inset: 8% 12%; background: rgba(8,11,18,.92);
       border: 1px solid rgba(255,255,255,.2); border-radius: 10px; padding: 20px 26px;
       pointer-events: auto; overflow-y: auto; }
.hud .build { position: absolute; left: 50%; top: 50%; transform: translate(-50%,-50%);
       width: 330px; background: rgba(8,11,18,.92); border: 1px solid rgba(255,255,255,.2);
       border-radius: 10px; padding: 18px 22px; pointer-events: auto; }
.hud .build h2 { margin: 0 0 4px; font-size: 16px; letter-spacing: .2em;
       text-transform: uppercase; color: #f0d060; }
.hud .build .sub { opacity: .7; margin-bottom: 10px; }
.hud .build .item { display: flex; justify-content: space-between; gap: 14px;
       border: 1px solid rgba(255,255,255,.18); border-radius: 6px;
       padding: 7px 10px; margin: 7px 0; }
.hud .build .item.buyable { border-color: #f0d060; cursor: pointer; }
.hud .build .item.buyable:hover { background: rgba(240,208,96,.12); }
.hud .build .item.blocked { opacity: .4; }
.hud .tech h2 { margin: 0 0 4px; font-size: 18px; letter-spacing: .2em;
       text-transform: uppercase; color: #f0d060; }
.hud .tech .sub { opacity: .7; margin-bottom: 14px; }
.hud .tech .cols { display: flex; gap: 22px; align-items: flex-start; }
.hud .tech .col { flex: 1; }
.hud .tech .col h3 { font-size: 12px; letter-spacing: .15em; text-transform: uppercase;
       opacity: .8; border-bottom: 1px solid rgba(255,255,255,.2); padding-bottom: 4px; }
.hud .tech .node { border: 1px solid rgba(255,255,255,.18); border-radius: 6px;
       padding: 7px 10px; margin: 8px 0; }
.hud .tech .node.owned { border-color: #6fbf5a; opacity: .8; }
.hud .tech .node.buyable { border-color: #f0d060; cursor: pointer; }
.hud .tech .node.buyable:hover { background: rgba(240,208,96,.12); }
.hud .tech .node.locked { opacity: .38; }
.hud .tech .node .top { display: flex; justify-content: space-between; }
.hud .tech .node .desc { opacity: .75; font-size: 11px; margin-top: 2px; }
.hud .lock { position: absolute; inset: 0; display: flex; align-items: center;
       justify-content: center; font-size: 20px; background: rgba(5,8,14,.45);
       letter-spacing: .05em; }
.hud .result { position: absolute; inset: 0; display: flex; flex-direction: column;
       align-items: center; justify-content: center; gap: 12px;
       background: rgba(5,8,14,.6); }
.hud .result .title { font-size: 52px; font-weight: 700; letter-spacing: .25em; }
.hud .result.victory .title { color: #f0d060; }
.hud .result.defeat .title { color: #d05a5a; }
.hud .result.draw .title { color: #b8bcc4; }
.hud .result .sub { font-size: 14px; opacity: .8; }
.hud .postresult { position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
       background: rgba(13,19,36,.7); border: 1px solid rgba(110,145,220,.3);
       border-radius: 6px; padding: 6px 16px; font-size: 13px;
       letter-spacing: .12em; text-transform: uppercase; }
.hud .postresult.victory { color: #f0d060; }
.hud .postresult.defeat { color: #d05a5a; }
.hud .postresult.draw { color: #b8bcc4; }
.hidden { display: none !important; }
`;

export class Hud {
  private roster: HTMLElement;
  private units: HTMLElement;
  private army: HTMLElement;
  private shop: HTMLElement;
  private build: HTMLElement;
  private map: HTMLCanvasElement;
  private fps: HTMLElement;
  private alert: HTMLElement;
  private tech: HTMLElement;
  private lockOverlay: HTMLElement;
  private resultOverlay: HTMLElement;
  private resultTitle: HTMLElement;
  private postResult: HTMLElement;
  /** When the match was decided (the big banner yields to a chip after 5s). */
  private resultSince: number | null = null;
  /** Signature of tech states; the panel DOM is only rebuilt when it changes
   * (rebuilding every frame destroyed nodes mid-click and flickered hover). */
  private techSignature = '';
  private buildSignature = '';
  /** "Click to take control" teaches the pointer-lock click exactly once;
   * after the first grab it never reappears. */
  private everLocked = false;
  techOpen = false;
  buildOpen = false;
  /** Set by the shell; called with a tech id when the player clicks one. */
  onResearch: (id: string) => void = () => {};
  /** Set by the shell; called with a purchase kind from the build menu. */
  onBuy: (kind: PurchaseKind) => void = () => {};

  constructor(container: HTMLElement) {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    const root = document.createElement('div');
    root.className = 'hud';
    root.innerHTML = `
      <div class="roster"></div>
      <div class="units panel"></div>
      <div class="hint">LMB light · Shift+LMB heavy · RMB block/parry · F kick ·
        Space dodge · Tab switch hero · 1-4 posture · B build · T tech · Enter restart</div>
      <div class="army panel"></div>
      <div class="shop panel"></div>
      <div class="fps"></div>
      <div class="alert hidden">⚔ YOUR KING IS IN DANGER ⚔</div>
      <canvas class="map" width="196" height="120"></canvas>
      <div class="tech hidden"></div>
      <div class="build hidden"></div>
      <div class="lock">Click to take control</div>
      <div class="result hidden"><div class="title"></div>
        <div class="sub">Press Enter for a new war — or keep playing</div></div>
      <div class="postresult hidden"></div>`;
    container.appendChild(root);

    this.roster = root.querySelector('.roster')!;
    this.units = root.querySelector('.units')!;
    this.army = root.querySelector('.army')!;
    this.shop = root.querySelector('.shop')!;
    this.build = root.querySelector('.build')!;
    this.map = root.querySelector('.map')!;
    this.fps = root.querySelector('.fps')!;
    this.alert = root.querySelector('.alert')!;
    this.tech = root.querySelector('.tech')!;
    this.lockOverlay = root.querySelector('.lock')!;
    this.resultOverlay = root.querySelector('.result')!;
    this.resultTitle = root.querySelector('.result .title')!;
    this.postResult = root.querySelector('.postresult')!;

    // pointerdown, not click: after exiting pointer lock the browser can
    // swallow the first full click pair, but pointerdown still fires
    this.tech.addEventListener('pointerdown', (e) => {
      const node = (e.target as HTMLElement).closest('.node.buyable');
      if (node) this.onResearch((node as HTMLElement).dataset.tech!);
    });
    this.build.addEventListener('pointerdown', (e) => {
      const node = (e.target as HTMLElement).closest('.item.buyable');
      if (node) this.onBuy((node as HTMLElement).dataset.buy! as PurchaseKind);
    });
  }

  setTechOpen(open: boolean): void {
    this.techOpen = open;
    this.tech.classList.toggle('hidden', !open);
    if (!open) this.techSignature = '';
    if (open) this.setBuildOpen(false);
  }

  setBuildOpen(open: boolean): void {
    this.buildOpen = open;
    this.build.classList.toggle('hidden', !open);
    if (!open) this.buildSignature = '';
    if (open) {
      this.techOpen = false;
      this.tech.classList.add('hidden');
      this.techSignature = '';
    }
  }

  update(state: GameState, pointerLocked: boolean, fps: number, kingAlert: boolean): void {
    this.fps.textContent = `${Math.round(fps)} fps`;
    this.alert.classList.toggle('hidden', !kingAlert);

    // roster: avatars
    this.roster.innerHTML = Object.values(state.characters)
      .filter((c) => c.brain === 'player')
      .map((c) => {
        const hp = Math.round((c.health / c.maxHealth) * 100);
        const sp = Math.round((c.stamina / PROFILES[c.profile].maximumStamina) * 100);
        const cls = ['char', c.id === state.controlled ? 'controlled' : '', c.alive ? '' : 'dead']
          .filter(Boolean)
          .join(' ');
        const staminaCls = c.exhausted ? 'fill stamina exhausted' : 'fill stamina';
        let label = c.id;
        if (!c.alive) {
          label +=
            c.respawnAtTick !== null
              ? ` — respawn ${Math.max(0, Math.ceil((c.respawnAtTick - state.tick) / TICK_RATE))}s`
              : ' — down';
        }
        return `<div class="${cls}"><div class="name">${label}</div>
          <div class="bar"><div class="fill" style="width:${hp}%"></div></div>
          <div class="bar"><div class="${staminaCls}" style="width:${sp}%"></div></div></div>`;
      })
      .join('');

    // units & squads panel (counts + per-squad objective and morale)
    const types: { label: string; archetype: string }[] = [
      { label: 'Infantry', archetype: 'infantry' },
      { label: 'Archers', archetype: 'archer' },
      { label: 'Knights', archetype: 'knight' },
    ];
    const typeRows = types
      .map((t) => {
        let count = 0;
        for (const c of Object.values(state.characters)) {
          if (c.alive && c.team === 'friendly' && c.brain === 'soldier' && c.archetype === t.archetype)
            count++;
        }
        return `<div class="row"><span>${t.label}</span><span>${count}</span></div>`;
      })
      .join('');
    const squadRows = Object.values(state.squads)
      .filter((q) => q.team === 'friendly')
      .map((q, i) => {
        let alive = 0;
        let morale = 0;
        for (const c of Object.values(state.characters)) {
          if (c.alive && c.squadId === q.id) {
            alive++;
            morale += c.morale;
          }
        }
        const avg = alive > 0 ? morale / alive : 0;
        const cls = avg < 0.3 ? 'broken' : avg < 0.55 ? 'low' : '';
        const leader = squadLeader(state, q.id);
        const led = leader && leader.id === state.controlled ? ' ◀' : '';
        return `<div class="row"><span>Squad ${i + 1}${led}
          <span class="morale"><i class="${cls}" style="width:${Math.round(avg * 100)}%"></i></span></span>
          <span>${alive} · ${q.objective}</span></div>`;
      })
      .join('');
    this.units.innerHTML = `<div class="head">Your Forces</div>${typeRows}
      <div class="head" style="margin-top:6px">Squads — Tab to lead one</div>${squadRows}`;

    this.drawMap(state);

    // army posture + gold
    const gold = Math.floor(state.gold.friendly);
    const income = state.goldIncome.friendly;
    this.army.innerHTML = `<span class="order">Army: ${state.posture.friendly}</span>
      &nbsp; <span class="key">1</span> defend · <span class="key">2</span> attack ·
      <span class="key">3</span> raid · <span class="key">4</span> rally<br>
      <span class="gold">${gold}g</span> (+${income}/s)`;

    // compact muster status; the real menu opens with B
    const pop = alivePop(state, 'friendly');
    this.shop.innerHTML = `<div class="head">Army ${pop}/${popCap(state, 'friendly')}</div>
      <div class="row"><span><span class="key">B</span> muster &amp; build</span></div>
      <div class="row"><span><span class="key">T</span> tech tree</span></div>`;

    if (this.techOpen) this.renderTech(state);
    if (this.buildOpen) this.renderBuild(state);

    if (pointerLocked) this.everLocked = true;
    const over = state.result !== 'playing';
    this.lockOverlay.classList.toggle(
      'hidden',
      pointerLocked || this.everLocked || over || this.techOpen || this.buildOpen
    );

    // the decisive moment gets the big banner; then it shrinks to a chip and
    // the player keeps playing in the world they won (or lost)
    if (!over) {
      this.resultSince = null;
    } else if (this.resultSince === null) {
      this.resultSince = performance.now();
    }
    const showBig = over && performance.now() - (this.resultSince ?? 0) < 5000;
    this.resultOverlay.classList.toggle('hidden', !showBig);
    this.postResult.classList.toggle('hidden', !over || showBig);
    if (over) {
      this.resultOverlay.classList.remove('victory', 'defeat', 'draw');
      this.resultOverlay.classList.add(state.result);
      this.resultTitle.textContent = state.result.toUpperCase();
      this.postResult.classList.remove('victory', 'defeat', 'draw');
      this.postResult.classList.add(state.result);
      this.postResult.textContent = `${state.result} — Enter for a new war`;
    }
  }

  /** Minimap: castles, buildings, every living unit as a team-colored dot. */
  private drawMap(state: GameState): void {
    const ctx = this.map.getContext('2d');
    if (!ctx) return;
    const w = this.map.width;
    const h = this.map.height;
    const sx = (x: number) => ((x + WORLD_HALF_X) / (WORLD_HALF_X * 2)) * w;
    const sy = (z: number) => ((WORLD_HALF_Z - z) / (WORLD_HALF_Z * 2)) * h;
    ctx.clearRect(0, 0, w, h);
    // castles
    for (const sign of [-1, 1]) {
      ctx.fillStyle = 'rgba(150,150,162,.75)';
      const x0 = sx(sign * 162);
      const x1 = sx(sign * 238);
      ctx.fillRect(Math.min(x0, x1), sy(58), Math.abs(x1 - x0), sy(-58) - sy(58));
      ctx.fillStyle = sign === -1 ? '#3a6fe0' : '#d93434';
      const k0 = sx(sign * 196);
      const k1 = sx(sign * 234);
      ctx.fillRect(Math.min(k0, k1), sy(32), Math.abs(k1 - k0), sy(-32) - sy(32));
    }
    // buildings
    for (const b of state.buildings) {
      const p = plotOf(b.team, b);
      ctx.fillStyle = b.team === 'friendly' ? '#7ba0ea' : '#e08a8a';
      ctx.fillRect(sx(p.x) - 1.5, sy(p.z) - 1.5, 3, 3);
    }
    // units
    for (const c of Object.values(state.characters)) {
      if (!c.alive) continue;
      if (c.kind === 'king') {
        ctx.fillStyle = '#f0d060';
        ctx.fillRect(sx(c.x) - 2, sy(c.z) - 2, 4, 4);
        continue;
      }
      if (c.id === state.controlled) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(sx(c.x) - 2, sy(c.z) - 2, 4, 4);
        continue;
      }
      ctx.fillStyle = c.team === 'friendly' ? '#3a6fe0' : '#d93434';
      ctx.fillRect(sx(c.x) - 1, sy(c.z) - 1, 2, 2);
    }
  }

  private renderBuild(state: GameState): void {
    const pop = alivePop(state, 'friendly');
    const cap = popCap(state, 'friendly');
    const kinds: PurchaseKind[] = [
      'infantry',
      'archers',
      'knights',
      'farm',
      'market',
      'smithy',
      'range',
      'chapel',
      'treasury',
      'housing',
    ];
    const singletons = ['smithy', 'range', 'chapel', 'treasury'];
    const states = kinds.map((kind) => {
      const item = SHOP[kind];
      const count =
        item.kind === 'building'
          ? state.buildings.filter((b) => b.team === 'friendly' && b.kind === item.building)
              .length
          : 0;
      const built =
        item.kind === 'building' &&
        ((singletons.includes(item.building) && count >= 1) ||
          (item.building === 'housing' && count >= 2));
      const locked = !waveUnlocked(state, 'friendly', kind);
      const cost =
        item.kind === 'building' ? buildingCost(state, 'friendly', item.building) : item.cost;
      const blocked =
        built ||
        locked ||
        state.gold.friendly < cost ||
        (item.kind === 'wave' && pop + item.count > cap);
      return { kind, item, locked, built, cost, blocked };
    });
    const signature = states
      .map((s) => `${s.kind}:${s.cost}:${s.blocked}:${s.locked}:${s.built}`)
      .join('|');
    if (signature !== this.buildSignature) {
      this.buildSignature = signature;
      const rows = states
        .map(({ kind, item, locked, built, cost, blocked }) => {
          const desc = item.kind === 'building' ? item.desc : '';
          const costText = built
            ? 'built'
            : locked
              ? kind === 'archers'
                ? 'needs range'
                : 'needs tech'
              : `${cost}g`;
          const cls = blocked ? 'item blocked' : 'item buyable';
          return `<div class="${cls}" data-buy="${kind}">
            <span>${item.label}${desc ? ` <span style="opacity:.65">— ${desc}</span>` : ''}</span>
            <span>${costText}</span></div>`;
        })
        .join('');
      this.build.innerHTML = `<h2>Muster &amp; Build</h2>
        <div class="sub">Army <span class="buildpop"></span>/${cap}
        · gold <span class="gold buildgold"></span> · B to close</div>${rows}`;
    }
    const goldEl = this.build.querySelector('.buildgold');
    if (goldEl) goldEl.textContent = `${Math.floor(state.gold.friendly)}g`;
    const popEl = this.build.querySelector('.buildpop');
    if (popEl) popEl.textContent = `${pop}`;
  }

  private renderTech(state: GameState): void {
    const team: Team = 'friendly';
    // per-tech state string; the DOM only rebuilds when this changes
    const reasons = TECHS.map((t) => techBlockReason(state, team, t.id));
    const states = TECHS.map((t, i) => {
      if (hasTech(state, team, t.id)) return 'o';
      if (reasons[i] !== null) return 'l';
      return state.gold[team] >= t.cost ? 'b' : 'a';
    });
    const signature = states.join('') + '|' + reasons.join('|');

    if (signature !== this.techSignature) {
      this.techSignature = signature;
      const branches = [
        ['military', 'Military'],
        ['economy', 'Economy'],
        ['royal', 'Royal Court'],
      ] as const;
      const cols = branches
        .map(([branch, title]) => {
          const nodes = TECHS.map((t, i) => ({ t, s: states[i], reason: reasons[i] }))
            .filter(({ t }) => t.branch === branch)
            .map(({ t, s, reason }) => {
              const cls =
                s === 'o' ? 'owned' : s === 'b' ? 'buyable' : s === 'a' ? 'available' : 'locked';
              const right = s === 'o' ? '✓' : `${t.cost}g`;
              const indent = t.prereqs.length > 0 ? ' style="margin-left:12px"' : '';
              const req =
                s === 'l' && reason && reason !== 'owned'
                  ? `<div class="desc" style="color:#d0985a">⚠ ${reason}</div>`
                  : '';
              return `<div class="node ${cls}" data-tech="${t.id}"${indent}>
                <div class="top"><span>${t.prereqs.length > 0 ? '└ ' : ''}${t.label}</span><span>${right}</span></div>
                <div class="desc">${t.desc}</div>${req}</div>`;
            })
            .join('');
          return `<div class="col"><h3>${title}</h3>${nodes}</div>`;
        })
        .join('');
      this.tech.innerHTML = `<h2>Tech Tree</h2>
        <div class="sub">Gold: <span class="gold techgold"></span>
        · click a highlighted tech to research · T to close</div>
        <div class="cols">${cols}</div>`;
    }

    // gold readout updates via textContent so the node tree stays stable
    const goldEl = this.tech.querySelector('.techgold');
    if (goldEl) goldEl.textContent = `${Math.floor(state.gold.friendly)}g`;
  }
}
